import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CheckSquare, Bot, User } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI } from '../../api';

const CreateTaskDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId, 
  teamMembers = [], 
  aiAgents = [],
  onSuccess 
}) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    assigned_to: '',
    assigned_ai_agent_id: '',
    deliverable_type: 'document'  // document, spreadsheet, report, email_draft
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    
    if (!contactId) {
      toast.error('Contact ID is missing');
      console.error('contactId is undefined');
      return;
    }

    try {
      setLoading(true);
      
      // Determine assignment type and name
      let assignedToType = 'user';
      let assignedToName = '';
      let assignedTo = form.assigned_to;
      
      if (form.assigned_ai_agent_id) {
        assignedToType = 'ai_agent';
        assignedTo = form.assigned_ai_agent_id;
        const agent = aiAgents.find(a => a.id === form.assigned_ai_agent_id);
        assignedToName = agent?.name || agent?.display_name || agent?.role_title || 'AI Agent';
      } else if (form.assigned_to) {
        const member = teamMembers.find(m => m.id === form.assigned_to);
        assignedToName = member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email : '';
      }
      
      const taskData = {
        title: form.title,
        description: form.description || '',
        priority: form.priority,
        status: 'pending',
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        assigned_to: assignedTo || null,
        assigned_to_type: assignedToType,
        assigned_to_name: assignedToName || null,
        // Include deliverable type for AI agent tasks
        deliverable_type: assignedToType === 'ai_agent' ? form.deliverable_type : null
      };
      
      console.log('Creating task with data:', taskData, 'for contact:', contactId);
      
      await contactsAPI.createTask(contactId, taskData);
      toast.success('Task created' + (assignedTo ? ' and assigned' : ''));
      
      // Reset form
      setForm({
        title: '',
        description: '',
        due_date: '',
        priority: 'medium',
        assigned_to: '',
        assigned_ai_agent_id: '',
        deliverable_type: 'document'
      });
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to create task:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Extract error message properly
      let errorMsg = 'Unknown error';
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data === 'string') {
          errorMsg = data;
        } else if (data.detail) {
          // FastAPI validation errors have detail as array or string
          if (Array.isArray(data.detail)) {
            errorMsg = data.detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
          } else if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          } else {
            errorMsg = JSON.stringify(data.detail);
          }
        } else if (data.message) {
          errorMsg = data.message;
        } else {
          errorMsg = JSON.stringify(data);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(`Failed to create task: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, [form, contactId, teamMembers, aiAgents, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({
      title: '',
      description: '',
      due_date: '',
      priority: 'medium',
      assigned_to: '',
      assigned_ai_agent_id: '',
      deliverable_type: 'document'
    });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-blue-600" />
            Create Task
          </DialogTitle>
          <DialogDescription>
            Create a new task for this contact
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="task-title">Title *</Label>
            <Input
              id="task-title"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter task title"
              data-testid="task-title-input"
              autoFocus
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="task-description">Description</Label>
            <Textarea
              id="task-description"
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter task description"
              rows={3}
              data-testid="task-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="task-due-date">Due Date</Label>
              <Input
                id="task-due-date"
                type="date"
                value={form.due_date}
                onChange={(e) => setForm(prev => ({ ...prev, due_date: e.target.value }))}
                data-testid="task-due-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="task-priority">Priority</Label>
              <Select 
                value={form.priority} 
                onValueChange={(val) => setForm(prev => ({ ...prev, priority: val }))}
              >
                <SelectTrigger id="task-priority" data-testid="task-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Assignment Section with Clear Headers */}
          <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
            <Label className="font-semibold text-base">Task Assignment</Label>
            
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-2">
                <User className="h-4 w-4 text-blue-600" />
                Assign to Team Member
              </Label>
              <Select 
                value={form.assigned_to || "unassigned"} 
                onValueChange={(val) => setForm(prev => ({ ...prev, assigned_to: val === "unassigned" ? '' : val, assigned_ai_agent_id: '' }))}
              >
                <SelectTrigger data-testid="task-assign-human-select">
                  <SelectValue placeholder="Select team member..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">-- Unassigned --</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name} {member.email ? `(${member.email})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-slate-50 dark:bg-slate-900 px-2 text-muted-foreground">or</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-2">
                <Bot className="h-4 w-4 text-purple-600" />
                Assign to AI Agent
              </Label>
              <Select 
                value={form.assigned_ai_agent_id || "none"} 
                onValueChange={(val) => setForm(prev => ({ ...prev, assigned_ai_agent_id: val === "none" ? '' : val, assigned_to: '' }))}
              >
                <SelectTrigger data-testid="task-assign-ai-select">
                  <SelectValue placeholder="Select AI agent..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No AI Agent --</SelectItem>
                  {aiAgents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name || agent.display_name} - {agent.role_title || 'AI Assistant'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Deliverable Type - Only show when AI agent is selected */}
            {form.assigned_ai_agent_id && (
              <div className="space-y-2 mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <Label className="text-sm font-medium flex items-center gap-2">
                  📄 AI Output Format
                </Label>
                <Select 
                  value={form.deliverable_type} 
                  onValueChange={(val) => setForm(prev => ({ ...prev, deliverable_type: val }))}
                >
                  <SelectTrigger data-testid="task-deliverable-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Word Document (.docx)</SelectItem>
                    <SelectItem value="spreadsheet">Excel Spreadsheet (.xlsx)</SelectItem>
                    <SelectItem value="report">Detailed Report (.docx)</SelectItem>
                    <SelectItem value="email_draft">Email Draft (.docx)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The AI will generate this file type as the deliverable
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !form.title.trim()} data-testid="create-task-submit">
              {loading ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

CreateTaskDialog.displayName = 'CreateTaskDialog';

export default CreateTaskDialog;
